import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Download, History, Settings, LogOut } from "lucide-react";
import { ThemeToggle } from "@/components/theme-toggle";
import { useAuth } from "@/hooks/useAuth";

export default function Dashboard({ user, onLogout }) {
  const location = useLocation();
  const { authService, isAuthenticated } = useAuth();
  const [profileUrl, setProfileUrl] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [currentDownload, setCurrentDownload] = useState(null);
  const [recentDownloads, setRecentDownloads] = useState([]);



  useEffect(() => {
    fetchRecentDownloads();
  }, []);

  useEffect(() => {
    if (currentDownload && currentDownload.status !== "completed" && currentDownload.status !== "failed") {
      const interval = setInterval(() => {
        checkDownloadStatus(currentDownload.id);
      }, 2000);
      return () => clearInterval(interval);
    }
  }, [currentDownload]);

  const fetchRecentDownloads = async () => {
    try {
      const result = await authService.apiRequest('/downloads');
      if (result.success) {
        // The API returns an array directly, not wrapped in an object
        setRecentDownloads(Array.isArray(result.data) ? result.data.slice(0, 3) : []);
      } else {
        console.error("Failed to fetch downloads:", result.error);
        toast.error("Failed to load recent downloads");
      }
    } catch (error) {
      console.error("Failed to fetch downloads", error);
      toast.error("Failed to load recent downloads");
    }
  };

  const checkDownloadStatus = async (downloadId) => {
    try {
      const result = await authService.apiRequest(`/downloads/${downloadId}`);
      if (result.success) {
        setCurrentDownload(result.data);
        
        if (result.data.status === "completed") {
          toast.success(`Download completed! ${result.data.images.length} images downloaded`);
          fetchRecentDownloads();
        } else if (result.data.status === "failed") {
          toast.error(`Download failed: ${result.data.error || 'Unknown error'}`);
        }
      } else {
        console.error("Failed to check status:", result.error);
        toast.error("Failed to check download status");
      }
    } catch (error) {
      console.error("Failed to check status", error);
      toast.error("Failed to check download status");
    }
  };

  const validatePixivUrl = (url) => {
    // Clean the URL first
    const cleanUrl = url.trim();
    
    // Accept various Pixiv URL formats
    const patterns = [
      /https?:\/\/(www\.)?pixiv\.net\/users\/\d+/i,           // https://www.pixiv.net/users/123456
      /https?:\/\/(www\.)?pixiv\.net\/en\/users\/\d+/i,       // https://www.pixiv.net/en/users/123456
      /https?:\/\/(www\.)?pixiv\.net\/member\.php\?id=\d+/i,  // https://www.pixiv.net/member.php?id=123456
      /(www\.)?pixiv\.net\/users\/\d+/i,                      // pixiv.net/users/123456 (without protocol)
      /(www\.)?pixiv\.net\/en\/users\/\d+/i,                  // pixiv.net/en/users/123456 (without protocol)
      /pixiv\.net\/member\.php\?id=\d+/i                      // pixiv.net/member.php?id=123456 (without protocol)
    ];
    
    return patterns.some(pattern => pattern.test(cleanUrl));
  };

  const normalizePixivUrl = (url) => {
    // Ensure URL has protocol
    if (!url.startsWith('http')) {
      url = 'https://' + url;
    }
    
    // Convert old member.php format to new users format
    const memberMatch = url.match(/member\.php\?id=(\d+)/);
    if (memberMatch) {
      return `https://www.pixiv.net/users/${memberMatch[1]}`;
    }
    
    // Ensure www. prefix for consistency
    if (url.includes('pixiv.net') && !url.includes('www.pixiv.net')) {
      url = url.replace('pixiv.net', 'www.pixiv.net');
    }
    
    return url;
  };

  const handleDownload = async () => {
    if (!profileUrl.trim()) {
      toast.error("Please enter a Pixiv profile URL");
      return;
    }



    // Validate URL format
    if (!validatePixivUrl(profileUrl)) {
      toast.error("Invalid Pixiv URL format. Please use: https://www.pixiv.net/users/123456 or similar format");
      return;
    }

    // Normalize the URL
    const normalizedUrl = normalizePixivUrl(profileUrl);

    setDownloading(true);
    try {
      const result = await authService.apiRequest('/pixiv/download', {
        method: 'POST',
        body: JSON.stringify({
          profile_url: normalizedUrl
        })
      });
      
      if (result.success) {
        setCurrentDownload(result.data);
        toast.success("Download started!");
        setProfileUrl("");
      } else {
        toast.error(result.error || "Failed to start download");
      }
    } catch (error) {
      console.error("Download error:", error);
      const message = error.message || "Failed to start download";
      toast.error(message);
    } finally {
      setDownloading(false);
    }
  };

  const getProgressPercentage = () => {
    if (!currentDownload) return 0;
    if (currentDownload.status === "pending") return 10;
    if (currentDownload.status === "in_progress") return 60;
    if (currentDownload.status === "completed") return 100;
    return 0;
  };

  return (
    <div className="dashboard-container" data-testid="dashboard">
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <Download size={28} color="#0ea5e9" />
          <h1>Pixiv Downloader</h1>
        </div>
        <nav className="dashboard-nav">
          <Link
            to="/"
            className={`nav-link ${location.pathname === "/" ? "active" : ""}`}
            data-testid="nav-dashboard"
          >
            Dashboard
          </Link>
          <Link
            to="/history"
            className={`nav-link ${location.pathname === "/history" ? "active" : ""}`}
            data-testid="nav-history"
          >
            History
          </Link>
          <Link
            to="/settings"
            className={`nav-link ${location.pathname === "/settings" ? "active" : ""}`}
            data-testid="nav-settings"
          >
            Settings
          </Link>
          <ThemeToggle />
          <button
            onClick={onLogout}
            className="logout-button"
            data-testid="logout-button"
          >
            <LogOut size={16} style={{ display: 'inline', marginRight: '6px' }} />
            Logout
          </button>
        </nav>
      </header>

      <main className="dashboard-content">
        <section className="welcome-section">
          <h2 data-testid="welcome-text">Welcome back, {user?.username}!</h2>
          <p>Enter a Pixiv profile URL to download all artworks</p>
        </section>

        <div className="download-card">
          <h3>Start New Download</h3>
          <div style={{ background: '#e0f2fe', padding: '16px', borderRadius: '12px', marginBottom: '24px', fontSize: '14px', color: '#0c4a6e' }}>
            <strong>How to use:</strong> Enter any Pixiv profile URL and click Download. Supported formats:
            <br />• <code>https://www.pixiv.net/users/123456</code>
            <br />• <code>https://www.pixiv.net/en/users/123456</code>  
            <br />• <code>https://www.pixiv.net/member.php?id=123456</code>
            <br />The app will scrape all artworks from the profile and upload them to ImgBB for permanent storage.
          </div>
          <div className="url-input-group">
            <input
              data-testid="profile-url-input"
              type="text"
              className="url-input"
              placeholder="https://www.pixiv.net/users/123456 or pixiv.net/en/users/123456"
              value={profileUrl}
              onChange={(e) => setProfileUrl(e.target.value)}
              disabled={downloading}
            />
            <button
              data-testid="start-download-button"
              className="download-button"
              onClick={handleDownload}
              disabled={downloading}
            >
              {downloading ? "Starting..." : "Download"}
            </button>
          </div>

          {currentDownload && (
            <div className="progress-section" data-testid="progress-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#0f172a' }}>Download Progress</h4>
                <span className={`status-badge ${currentDownload.status}`} data-testid="download-status">
                  {currentDownload.status.replace('_', ' ')}
                </span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar"
                  style={{ width: `${getProgressPercentage()}%` }}
                  data-testid="progress-bar"
                ></div>
              </div>
              <p className="status-text">
                {currentDownload.status === "completed" && `${currentDownload.images.length} images downloaded and uploaded to ImgBB`}
                {currentDownload.status === "in_progress" && "Scraping Pixiv profile and uploading to ImgBB..."}
                {currentDownload.status === "pending" && "Preparing download..."}
                {currentDownload.status === "failed" && "Download failed. Please check the URL and try again."}
              </p>
              
              {currentDownload.status === "completed" && currentDownload.images.length > 0 && (
                <div className="image-gallery" data-testid="image-gallery">
                  {currentDownload.images.slice(0, 6).map((image, index) => (
                    <div key={image.id} className="gallery-image" data-testid={`gallery-image-${index}`}>
                      <img src={image.thumbnail} alt={image.title} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {recentDownloads.length > 0 && (
          <section>
            <h3 style={{ fontSize: '24px', fontWeight: '600', color: '#0f172a', marginBottom: '24px' }}>Recent Downloads</h3>
            <div className="history-grid">
              {recentDownloads.map((download) => (
                <div key={download.id} className="history-item" data-testid="recent-download-item">
                  <div className="history-item-header">
                    <h4>{new URL(download.profile_url).hostname}</h4>
                    <span className={`status-badge ${download.status}`}>{download.status.replace('_', ' ')}</span>
                  </div>
                  <p className="history-item-meta">
                    {new Date(download.created_at).toLocaleString()}
                  </p>
                  {download.status === "completed" && (
                    <p className="image-count">{download.images.length} images</p>
                  )}
                </div>
              ))}
            </div>
          </section>
        )}
      </main>
    </div>
  );
}